<script>
import Paging from "@/components/Paging.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Api from "api-client";

export default {
  name: "Receipts",
  components: {Paging, ToggleSwitch},

  data() {
    return {
      result: {},
      searchTerm: "",
      filtersOpen: true,
      paidFilter: false,
    }
  },
  props: {
    companyId: {
      required: true,
      type: Number,
    }
  },

  computed: {
    hasCompanyId() {
      return this.companyId !== null && this.companyId !== undefined;
    }
  },

  methods: {
    doPage(data) {
      this.loadData(data.pagingPage - 1, data.pagingOffset);
    },

    loadData(page, size) {
      Api.getReceipts(
          page,
          size,
          this.companyId,
          this.searchTerm,
          this.paidFilter ? true : null
      )
          .then((resp) => (this.result = resp.data))
          .catch((error) => {
            console.error("API hiba:", error);
            this.$toast.error({
              title: this.$cte("common.error"),
              message: this.$cte("common.error"),
            });
          });
    },

    formatAmount(amount, currency) {
      if (amount === null || amount === undefined) {
        return '0';
      }
      const formattedAmount = amount.toLocaleString('hu-HU');
      const currencySymbol = currency || 'Ft';
      return `${formattedAmount} ${currencySymbol}`;
    },

    formatDate(dateString) {
      if (!dateString) {
        return '-';
      }
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}.`;
    },

    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
    },

    togglePaid(id) {
      Api.receiptTogglePaid(id)
          .then(() => {
            this.loadData(
                this.result.currentPage || 0,
                this.result.pageSize || 10
            );
          })
          .catch((error) => {
            console.error("API hiba:", error);
            this.$toast.error({
              title: this.$cte("common.error"),
              message: this.$cte("common.error"),
            });
          });
    },
  },

  watch: {
    searchTerm() {
      if (this.hasCompanyId) {
        this.loadData(0, 10);
      }
    },
    paidFilter() {
      if (this.hasCompanyId) {
        this.loadData(0, 10);
      }
    },
    companyId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadData(0, 10);
        }
      }
    }
  },

  mounted() {
    if (this.hasCompanyId) {
      this.loadData(0, 10);
    }
  },
}
</script>

<template>
  <div class="Receipts_vue main-wrapper m-0 p-0 mt-3 font-jumperlight container-fluid">

    <div class="mx-3 mt-2 mb-3">
      <b-form-input
          debounce="600"
          v-model="searchTerm"
          class="white-input d-inline-block align-middle"
          placeholder="Nyugtaazonosító"
          style="width: calc(100% - 50px);"
      ></b-form-input>
      <button
          class="btn btn-link ml-2 align-items-center bg-white p-2 border rounded-circle shadow-lg"
          @click="toggleFilters"
          aria-label="Toggle filters"
          style="vertical-align: middle; font-size: 1rem; width: 40px; height: 40px; border-color: #CED4DA !important; margin-top: -10px;"
      >
        <i class="fa-light fa-sliders"></i>
      </button>
    </div>

    <transition name="accordion-slide">
      <div v-show="filtersOpen" class="formFilters mb-3 px-3 mr-0 mr-md-5">
        <div class="d-flex align-items-center">
          <ToggleSwitch v-model="paidFilter"/>
          <div class="ml-2 mb-2 small">Rendezett</div>
        </div>
      </div>
    </transition>

    <div v-if="result && result.totalNumberOfElements === 0" class="mx-3">
      Nincsenek megjeleníthető adatok
    </div>

    <div v-else class="">

      <div v-for="(item) in result.content"
           :key="item.id"
           class="item-card mb-3">
        
        <!-- Row 1: Receipt Number -->
        <div class="card-row row-header">
          <div class="receipt-number font-weight-bold small">
            <span class="text-truncate">{{ item.receiptNumber }}</span>
          </div>
        </div>

        <!-- Row 2: Amount -->
        <div class="card-row row-amount">
          <div class="amount-value font-weight-bold">{{ formatAmount(item.grossTotalValue, item.currency) }}</div>
        </div>

        <!-- Row 3: Dates -->
        <div class="card-row row-dates">
          <div class="date-column start-date-column">
            <div class="label text-muted">Kezdő dátum</div>
            <div class="date-value font-weight-bold">{{ formatDate(item.startDate) }}</div>
          </div>
          <div class="date-column end-date-column">
            <div class="label text-muted">Vége dátum</div>
            <div class="date-value font-weight-bold">{{ formatDate(item.endDate) }}</div>
          </div>
        </div>

        <!-- Row 4: Fizetve Toggle -->
        <div class="card-row row-toggle">
          <div class="toggle-wrapper">
            <span class="toggle-label">Fizetve</span>
            <ToggleSwitch v-model="item.paid"
                          @change="() => togglePaid(item.id)"
                          @click.native.stop/>
          </div>
        </div>
      </div>
    </div>

    <paging
        v-if="result.totalNumberOfElements > 0"
        :item-per-page="result.pageSize"
        :total-rows="result.totalNumberOfElements"
        @paging-change="doPage"
    />

  </div>
</template>

<style scoped>
.main-wrapper {
  background-color: white;
  padding: 15px;
  border-radius: 5px;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Card Base Styles */
.item-card {
  background-color: #C8D5DA;
  border-radius: 36px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Card Rows */
.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Row 1: Header - Receipt Number */
.row-header {
  margin-bottom: 12px;
}

.receipt-number {
  font-size: 11px;
  color: #333;
}

/* Row 2: Amount */
.row-amount {
  margin-bottom: 12px;
}

.amount-value {
  font-size: 20px;
  color: #333;
}

/* Row 3: Dates */
.row-dates {
  margin-bottom: 12px;
}

.date-column {
  flex: 1;
}

.date-column .label {
  font-size: 11px;
  margin-bottom: 2px;
}

.date-value {
  font-size: 13px;
  color: #333;
}

/* Row 4: Toggle */
.row-toggle {
  justify-content: flex-end;
  align-items: center;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-size: 12px;
  color: #333;
}

/* Accordion Transition */
.accordion-slide-enter-active, .accordion-slide-leave-active {
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.accordion-slide-enter, .accordion-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-slide-enter-to, .accordion-slide-leave {
  max-height: 500px;
  opacity: 1;
}

button:focus {
  outline: none !important;
  box-shadow: none !important;
}

/* Desktop Styles (992px and up) */
@media (min-width: 992px) {
  .item-card {
    background-color: #AEC0C7;
    border-radius: 36px;
    padding: 12px 24px;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  .card-row {
    display: contents;
  }

  .row-header {
    flex: 0 0 auto;
    width: 30%;
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }

  .row-amount {
    flex: 0 0 auto;
    width: 13%;
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }

  .amount-value {
    font-size: 16px;
  }

  .row-dates {
    flex: 0 0 auto;
    width: 32%;
    display: flex;
    margin-bottom: 0;
  }

  .date-column {
    flex: 1;
  }

  .row-toggle {
    flex: 0 0 auto;
    width: 25%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 24px;
  }
}
</style>
